/**
 * Agent id of Sherlock, the findings investigator built on the Rain runtime.
 * Backs the finance case-file chat (see `useInvestigatorRainAdapter`).
 *
 * `sherlock`, NOT `sherlock-v1`. The CR's `metadata.name` is the versioned
 * snapshot (`{agent_id}-v{version}`); the logical id Rain routes on is the
 * `rain.io/agent` label, and the gateway route matches
 * `…/agents/sherlock/threads.*`. Same relationship as `smith` ↔ `smith-v*`.
 *
 * Sherlock is **workspace-scoped**: `sherlock.k8s.yaml` declares `organizationId`
 * and `workspaceId` variables, so its threads live at
 * `…/organizations/{org}/workspaces/{ws}/agents/sherlock/threads/…` with no
 * automation parent. It also declares an **optional** `workItemId`, which we pass
 * only when a thread opens from a Workbench work item (to scope Sherlock's
 * Workbench tools to it). See agent-sherlock's `sherlock.k8s.yaml`.
 */
export const RAIN_SHERLOCK_AGENT_ID = 'sherlock';

/**
 * Sherlock's evidence-gathering tools, by source. These are the *friendly*
 * names Rain exposes through the CR's metadata adapters, not the underlying
 * gRPC method names — `findings.v1.FindingsService.GetFinding` arrives as
 * `get_finding`.
 *
 * `plan_*` and `artifact_*` are absent: plan progress belongs to the Todo card
 * and artifact writes are internal scaffolding.
 */
export enum ESherlockTool {
  // Findings service (argus-findings) — the case under investigation.
  GetFinding = 'get_finding',
  ListFindings = 'list_findings',
  GetInvestigation = 'get_investigation',
  ListInvestigations = 'list_investigations',
  ListDetectors = 'list_detectors',
  // Argus entity/fact graph — the derived view Sherlock reasons over.
  GetEntity = 'get_entity',
  ListEntities = 'list_entities',
  SearchEntities = 'search_entities',
  LookupEntity = 'lookup_entity',
  TraverseEntity = 'traverse_entity',
  GetFact = 'get_fact',
  ListFacts = 'list_facts',
  ListFactHistory = 'list_fact_history',
  FindSharedNeighbors = 'find_shared_neighbors',
  ReverseResolution = 'reverse_resolution',
  GetOntology = 'get_ontology',
  GetConnector = 'get_connector',
  ListConnectors = 'list_connectors',
  GetSyncJob = 'get_sync_job',
  ListSyncJobs = 'list_sync_jobs',
  // BDK reads — reaching the system of record.
  ListConnections = 'list_connections',
  GetConnection = 'get_connection',
  BrowseDiscoverables = 'browse_discoverables',
  ListConnectionProcedures = 'list_connection_procedures',
  // Web corroboration (DuckDuckGo).
  WebFetch = 'web_fetch',
  WebSearch = 'web_search',
  // bdk integration catalog.
  SearchApps = 'search_apps',
  SearchProcedures = 'search_procedures',
  RequestConnection = 'request_connection',
  // Jarvis code sandbox.
  RunCode = 'run_code',
  SetDiscovered = 'set_discovered',
  PrepareCodeManifest = 'prepare_code_manifest',
  // Workbench — the work item being prepared and its decisions.
  GetWorkItem = 'get_work_item',
  ListWorkUnits = 'list_work_units',
  GetWorkUnit = 'get_work_unit',
  PublishDecisionForm = 'publish_decision_form',
  ProposeFindingDecision = 'propose_finding_decision',
  SubmitFindingDecisions = 'submit_finding_decisions',
  SearchGuide = 'search_guide',
  GetGuide = 'get_guide',
  CreateGuide = 'create_guide',
  // Grimoire — automations a decision can run.
  GetWsAutomations = 'get_ws_automations',
  GetAutomation = 'get_automation',
}

/** Tools Sherlock calls that the transcript deliberately leaves out. */
export enum ESherlockInternalTool {
  MarkWorkItemPreparationStarted = 'mark_work_item_preparation_started',
}

/** Sherlock's Workbench tools whose success leaves its work item's queries stale. */
export const SHERLOCK_WORKBENCH_WRITES: ReadonlySet<string> = new Set([
  ESherlockInternalTool.MarkWorkItemPreparationStarted,
  ESherlockTool.PublishDecisionForm,
  ESherlockTool.ProposeFindingDecision,
  ESherlockTool.SubmitFindingDecisions,
]);
